<template>
  <span>
    <KeyboardEvent v-on:keyup="keyboardEvent"></KeyboardEvent>
    <ViewBar :prominent="prominent" :contentUnder="contentUnder">
      <template #default>
        <SearchToolbar
            v-if="showSearchTools && !loading"
            :searchParams="searchParams"
            :totalHits="totalHits"
            :exactTotalHitCount="exactTotalHitCount"
            @click-first="onFirstDoc"
            @click-prev="onPrevDoc"
            @click-next="onNextDoc"
            @click-last="onLastDoc"
            @change-index="searchDocByIndex($event)"
            view="documents"
        >
          <template #actions>
            <v-text-field
                v-if="showSentenceFilters"
                v-model="sentenceFilter.maxSentences"
                class="mt-5 mr-5"
                style="max-width:100px"
                label="max sentences"
            ></v-text-field>
            <v-text-field
                v-model="sentenceFilter.minScore"
                v-if="showSentenceFilters"
                class="mt-5"
                style="max-width:100px"
                label="min score"
            ></v-text-field>
            <v-switch
                class="mt-5"
                v-if="currentDocumentHasSentenceCategories"
                v-model="showSentenceFilters"
            ></v-switch>
            <DocumentsActionMenu
                v-if="canDeleteDocuments || canExportDocuments || canModifyMetadata">
            </DocumentsActionMenu>
          </template>
        </SearchToolbar>
        <v-toolbar color="grey lighten-3" v-else flat dense>
          <v-progress-linear
              v-if="loading"
              :active="loading"
              rounded
              indeterminate
          ></v-progress-linear>
          <v-spacer v-if="!loading"></v-spacer>
          <DocumentsActionMenu
              v-if="!loading && canDeleteDocuments || canExportDocuments || canModifyMetadata">
          </DocumentsActionMenu>
        </v-toolbar>
      </template>
      <template #extension-content>
        <CategoryChips
          v-if="isClassificationProject && currentDocument"
          :categories="currentDocument.categories"
          :hideNonCategories="false"
          :ignoreCategories="false"
        ></CategoryChips>
      </template>
    </ViewBar>
    <v-container
      id="document-view"
      @mouseenter="enterLabelSelectionArea()"
      @mouseleave="leaveLabelSelectionArea()"
      fluid
    >
      <ViewBarVerticalPadding
        :prominent="prominent"
        :in-viewport-root-margin="prominent ? '-140px 0px' : '-80px 0px'"
        @content-hidden-by-viewbar="contentHiddenByViewBar()"
        @content-visible-below-viewbar="contentVisibleBelowViewBar()"
      ></ViewBarVerticalPadding>
      <v-container fluid id="maindoc">
        <JobCard
          v-if="docImportJobInProgress"
          :job="docImportJobInProgress"
        ></JobCard>
        <DocumentCard
          v-if="!docImportJobInProgress && !loading && currentDocument"
          :key="currentDocument.identifier"
          :doc="currentDocument"
        ></DocumentCard>
        <span v-else-if="!docImportJobInProgress && !loading">{{
          $t("explore.message.noresults")
        }}</span>
      </v-container>
    </v-container>
  </span>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {flattenMetadata} from "@/docUtils";
import tourMixin from "../tourUtils/tourMixin";
import DocumentCard from "../components/document/DocumentCard";
import KeyboardEvent from "../components/KeyboardEvent";
import CategoryChips from "../components/categories/CategoryChips";
import {decodeParams} from "@/routes/pathParams";

import ViewBar from "../components/viewbar/ViewBar";
import ViewBarVerticalPadding from "../components/viewbar/ViewBarVerticalPadding";

import labelShortcutsMixin from "../mixins/labelShortcutsMixin";
import JobCard from "../components/JobCard";
import DocumentsActionMenu from './DocumentsActionMenu.vue';
import SearchToolbar from "@/components/search/SearchToolbar";

export default {
  mixins: [labelShortcutsMixin],
  components: {
    SearchToolbar,
    DocumentCard,
    ViewBar,
    KeyboardEvent,
    ViewBarVerticalPadding,
    CategoryChips,
    JobCard,
    DocumentsActionMenu,
  },
  provide: function () {
    return {
      // injected into CategoryChips component of the DocumentCard
      createCategory: this.createCategory,
      deleteCategory: this.deleteCategory,
      acceptSuggestedCategory:  this.acceptSuggestedCategory,
      // injected into the DocummapActionsentCard
      skipSuggestion: () => {
        throw new Error("Unsupported in doc view");
      },
      validateDocument: () => {
        throw new Error("Unsupported in doc view");
      },
      // injected into QueryFacets component
      selectedFacetsChangedParams: this.selectedFacetsChangedParams,
      queryChangedParams: this.queryChangedParams,
    };
  },
  data() {
    return {
      prominent: false,
      showSearchTools: false,
      datasetLoading: false,
      contentUnder: false,
      newMetadata: { name: null, value: null },
      showSentenceFilters: false,
    };
  },
  created() {
    const path = this.$route.path;
    if (!path.startsWith("/document/")) this.showSearchTools = true;
    tourMixin.enhance(this, "shepherdNexter");
    this.shepherdNexter();
  },
  beforeMount() {
    this.resetStore()
  },
  mounted: function () {
    const params = this.$route.params.searchParams;
    this.searchDoc(params);
  },
  watch: {
    "$route.path": function (path) {
      this.showSearchTools = !path.startsWith("/document/");
    },
    "$route.params.searchParams": function (params) {
      this.searchDoc(params);
    },
    docUpdateJobInProgress: function (value) {
      if (!value) {
        this.refreshView()
      }
    },
    docUpdateAnnotationRemoved: function (value) {
      if (!value) {
        this.$router.push({
          name: "documents",
        });
      }
    },
  },
  computed: {
    ...mapGetters("auth", [
      "canDeleteDocuments",
      "canModifyMetadata",
      "canExportDocuments"
    ]),
    ...mapGetters("jobs", [
      "docImportJobInProgress",
      "docUpdateJobInProgress",
      "docUpdateAnnotationRemoved",
      ]),
    ...mapGetters(["isClassificationProject"]),
    ...mapState("document", [
      "sentenceFilter",
      "totalHits",
      "loading",
      "exactTotalHitCount",
      "searchParams",
    ]),
    ...mapGetters("document", [
      "currentDocument",
      "selectedFacetsChangedParams",
      "queryChangedParams",
      "firstDocParams",
      "prevDocParams",
      "docByIndexParams",
      "nextDocParams",
      "lastDocParams",
      "refreshParams",
    ]),
    ...mapGetters("settings", ["dark", "textColor", "toolbarColor"]),
    currentDocumentHasSentenceCategories() {
      return this.currentDocument && this.currentDocument.sentences && this.currentDocument.sentences.some(sentence => sentence.categories && sentence.categories.length > 0)
    }
  },
  methods: {
    ...mapMutations("document", ["resetStore"]),
    ...mapActions("settings", ["fetchLabels"]),
    ...mapActions("document", [
      "searchDocument",
      "createCategory",
      "deleteCategory",
    ]),
    refreshView() {
      // this can't be this.searchParams because of this useless search windows thing...
      // so we must fool the store that those are different search parameters
      const params = this.refreshParams();
      this.searchDoc(params);
    },
    keyboardEvent(e) {
      if (e.target.id !== "tb_searchSegments") {
        if (e.key === "ArrowRight") {
          this.onNextDoc();
        } else if (e.key === "ArrowLeft") {
          this.onPrevDoc();
        }
      }
    },
    async searchDoc(params) {
      const searchParams = decodeParams(params);
      await this.searchDocument(searchParams);
    },
    flattenMetadata,
    onFirstDoc() {
      const searchParams = this.firstDocParams();
      this.$router.push({
        name: "documentsByParams",
        params: { searchParams },
      });
    },
    onPrevDoc() {
      const searchParams = this.prevDocParams();
      this.$router.push({
        name: "documentsByParams",
        params: { searchParams },
      });
    },
    onNextDoc() {
      const searchParams = this.nextDocParams();
      this.$router.push({
        name: "documentsByParams",
        params: { searchParams },
      });
    },
    onLastDoc() {
      const searchParams = this.lastDocParams();
      this.$router.push({
        name: "documentsByParams",
        params: { searchParams },
      });
    },
    searchDocByIndex(newDocIndex) {
      const searchParams = this.docByIndexParams(newDocIndex - 1);
      this.$router.push({
        name: "documentsByParams",
        params: { searchParams },
      });
    },
    contentHiddenByViewBar() {
      // console.debug("contentHiddenByViewBar")
      if (this.isClassificationProject) {
        this.prominent = true;
      }
      this.contentUnder = true;
    },
    contentVisibleBelowViewBar() {
      // console.debug("contentVisibleBelowViewBar")
      if (this.isClassificationProject) {
        setTimeout(() => {
          this.prominent = false;
        }, 100);
      }
      this.contentUnder = false;
    },
  },
};
</script>
<style>
.sp_doctitle:hover {
  text-decoration: underline;
  cursor: pointer;
}

.searchHit {
  font-weight: bold;
}
</style>
